import { useState, useEffect } from 'react';
import { Settings, Bell, Moon, Sun, Globe, Monitor, Shield, HelpCircle, Check, X } from 'lucide-react';
import api from '../../api/apiClient';
import { useTheme } from '../../contexts/ThemeContext';

interface AppSettings {
  notifications: {
    email: boolean; sms: boolean; push: boolean;
    jobAssignments: boolean; quoteRequests: boolean; paymentUpdates: boolean;
  };
  appearance: { theme: 'light' | 'dark' | 'system'; compactMode: boolean; };
  privacy: { showOnlineStatus: boolean; readReceipts: boolean; };
  language: string;
  timezone: string;
}

const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
];

const LANGUAGES = [
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'en-US', label: 'English (US)' },
];

export default function ContractorSettings() {
  const [settings, setSettings] = useState<AppSettings>({
    notifications: { email: true, sms: false, push: true, jobAssignments: true, quoteRequests: true, paymentUpdates: true },
    appearance: { theme: 'light', compactMode: false },
    privacy: { showOnlineStatus: true, readReceipts: true },
    language: 'en-AU',
    timezone: 'Australia/Sydney',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const { setTheme } = useTheme();

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get<any>('/users/me/settings');
      if (response) setSettings(prev => ({ ...prev, ...response }));
    } catch (err) { console.log('Using default settings'); }
    finally { setLoading(false); }
  };

  const handleSaveSettings = async () => {
    setSaving(true); setError('');
    try {
      await api.put('/users/me/settings', settings);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) { setError(err.message || 'Failed to save settings'); }
    finally { setSaving(false); }
  };

  const updateNotification = (key: keyof AppSettings['notifications'], value: boolean) => {
    setSettings(prev => ({ ...prev, notifications: { ...prev.notifications, [key]: value } }));
  };


  const updateAppearance = (key: keyof AppSettings['appearance'], value: any) => {
    setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, [key]: value } }));
    if (key === 'theme') setTheme(value);
  };

  const updatePrivacy = (key: keyof AppSettings['privacy'], value: boolean) => {
    setSettings(prev => ({ ...prev, privacy: { ...prev.privacy, [key]: value } }));
  };

  if (loading) {
    return <div className="max-w-4xl mx-auto px-4 py-8"><div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div></div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Settings className="w-8 h-8 text-primary" />
        <div><h1 className="text-3xl font-bold text-gray-900">Settings</h1><p className="text-gray-600">Customize your app experience</p></div>
      </div>

      {success && <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3"><Check className="w-5 h-5 text-green-600" /><span className="text-green-800">{success}</span></div>}
      {error && <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3"><X className="w-5 h-5 text-red-600" /><span className="text-red-800">{error}</span><button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800"><X className="w-4 h-4" /></button></div>}

      <div className="space-y-6">
        {/* Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6"><Bell className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2></div>
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Notification Channels</h3>
              <div className="space-y-3">
                {[
                  { key: 'email', label: 'Email Notifications', desc: 'Receive updates via email' },
                  { key: 'sms', label: 'SMS Notifications', desc: 'Receive text messages for urgent updates' },
                  { key: 'push', label: 'Push Notifications', desc: 'Receive in-app notifications' },
                ].map(item => (
                  <label key={item.key} className="flex items-center justify-between cursor-pointer">
                    <div><p className="font-medium text-gray-900">{item.label}</p><p className="text-sm text-gray-500">{item.desc}</p></div>
                    <input type="checkbox" checked={settings.notifications[item.key as keyof typeof settings.notifications]} onChange={(e) => updateNotification(item.key as keyof AppSettings['notifications'], e.target.checked)} className="w-5 h-5 text-primary rounded focus:ring-secondary" />
                  </label>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Notification Types</h3>
              <div className="space-y-3">
                {[
                  { key: 'jobAssignments', label: 'Job Assignments', desc: 'New jobs assigned to you' },
                  { key: 'quoteRequests', label: 'Quote Requests', desc: 'Requests for quotes on jobs' },
                  { key: 'paymentUpdates', label: 'Payment Updates', desc: 'Invoice and payment notifications' },
                ].map(item => (
                  <label key={item.key} className="flex items-center justify-between cursor-pointer">
                    <div><p className="font-medium text-gray-900">{item.label}</p><p className="text-sm text-gray-500">{item.desc}</p></div>
                    <input type="checkbox" checked={settings.notifications[item.key as keyof typeof settings.notifications]} onChange={(e) => updateNotification(item.key as keyof AppSettings['notifications'], e.target.checked)} className="w-5 h-5 text-primary rounded focus:ring-secondary" />
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6"><Monitor className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold text-gray-900">Appearance</h2></div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
              <div className="flex gap-3">
                {[
                  { value: 'light', icon: Sun, label: 'Light' },
                  { value: 'dark', icon: Moon, label: 'Dark' },
                  { value: 'system', icon: Monitor, label: 'System' },
                ].map(item => (
                  <button key={item.value} onClick={() => updateAppearance('theme', item.value)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${settings.appearance.theme === item.value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'}`}>
                    <item.icon className="w-5 h-5" /><span>{item.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Note: Dark mode is coming soon.</p>
            </div>
            <label className="flex items-center justify-between cursor-pointer pt-2">
              <div><p className="font-medium text-gray-900">Compact Mode</p><p className="text-sm text-gray-500">Reduce spacing for more content</p></div>
              <input type="checkbox" checked={settings.appearance.compactMode} onChange={(e) => updateAppearance('compactMode', e.target.checked)} className="w-5 h-5 text-primary rounded focus:ring-secondary" />
            </label>
          </div>
        </div>

        {/* Language & Region */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6"><Globe className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold text-gray-900">Language & Region</h2></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
              <select value={settings.language} onChange={(e) => setSettings({ ...settings, language: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent">
                {LANGUAGES.map(lang => <option key={lang.value} value={lang.value}>{lang.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select value={settings.timezone} onChange={(e) => setSettings({ ...settings, timezone: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent">
                {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Privacy */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6"><Shield className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold text-gray-900">Privacy</h2></div>
          <div className="space-y-3">
            {[
              { key: 'showOnlineStatus', label: 'Show Online Status', desc: "Let others see when you're online" },
              { key: 'readReceipts', label: 'Read Receipts', desc: "Let others know when you've read their messages" },
            ].map(item => (
              <label key={item.key} className="flex items-center justify-between cursor-pointer">
                <div><p className="font-medium text-gray-900">{item.label}</p><p className="text-sm text-gray-500">{item.desc}</p></div>
                <input type="checkbox" checked={settings.privacy[item.key as keyof typeof settings.privacy]} onChange={(e) => updatePrivacy(item.key as keyof AppSettings['privacy'], e.target.checked)} className="w-5 h-5 text-primary rounded focus:ring-secondary" />
              </label>
            ))}
          </div>
        </div>

        {/* Help & Support */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-6"><HelpCircle className="w-5 h-5 text-primary" /><h2 className="text-lg font-semibold text-gray-900">Help & Support</h2></div>
          <div className="space-y-3">
            {[
              { href: 'https://help.rentry.com.au', label: 'Help Center' },
              { href: 'mailto:support@rentry.com.au', label: 'Contact Support' },
              { href: 'https://rentry.com.au/privacy', label: 'Privacy Policy' },
              { href: 'https://rentry.com.au/terms', label: 'Terms of Service' },
            ].map(item => (
              <a key={item.label} href={item.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                <span className="font-medium text-gray-900">{item.label}</span><span className="text-secondary">→</span>
              </a>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 text-center"><p className="text-sm text-gray-500">Rentry v1.0.0</p></div>
        </div>

        <div className="flex justify-end">
          <button onClick={handleSaveSettings} disabled={saving} className="px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 font-medium">{saving ? 'Saving...' : 'Save Settings'}</button>
        </div>
      </div>
    </div>
  );
}

